<script setup lang="ts">
const props = defineProps<{
  active: string
  sections?: string[]
}>()
const emit = defineEmits<{ go: [string] }>()
const showScrollTop = ref(false)
let heroObserver: IntersectionObserver | undefined

// لوگوی برند کاربر برای نشان‌دادن در بالای ریل؛ ممکن است رشته یا آبجکت رسانه باشد.
const p = usePortfolioStore().profile
const logo = computed(() => resolveMediaUrl(p?.logo))
const logoAlt = computed(() => (p?.fullname ? `لوگوی ${p.fullname}` : 'لوگو'))

const items = [
  { id: 'hero', label: 'خانه', d: 'M3 10l9-7 9 7v9a2 2 0 01-2 2h-4v-6H9v6H5a2 2 0 01-2-2z' },
  { id: 'about', label: 'درباره من', d: 'M12 12a4 4 0 100-8 4 4 0 000 8zM4 21a8 8 0 0116 0' },
  { id: 'skills', label: 'مهارت‌ها', d: 'M12 2l3 6 6 1-4.5 4.2L18 20l-6-3-6 3 1.5-6.8L3 9l6-1z' },
  { id: 'services', label: 'خدمات', d: 'M4 7h16v13H4zM9 7V4h6v3' },
  { id: 'projects', label: 'پروژه‌ها', d: 'M3 7h6l2 2h10v10H3z' },
  { id: 'contact', label: 'تماس', d: 'M3 6h18v12H3zM3 6l9 7 9-7' }
]
const visibleItems = computed(() =>
  Array.isArray(props.sections)
    ? items.filter(
        item =>
          ['about', 'skills'].includes(item.id) ||
          props.sections.includes(item.id)
      )
    : items
)

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

onMounted(() => {
  const hero = document.getElementById('hero')
  if (!hero) return

  heroObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry) return
      showScrollTop.value = !entry.isIntersecting && entry.boundingClientRect.top < 0
    },
    { threshold: 0.05 }
  )
  heroObserver.observe(hero)
})

onBeforeUnmount(() => heroObserver?.disconnect())
</script>

<template>
  <div class="sticky top-0 z-50 mx-auto w-full shrink-0 self-start md:top-1/2 md:mx-0 md:w-14 md:-translate-y-1/2">
    <nav
      class="flex w-full flex-row flex-wrap justify-center gap-2 rounded-b-2xl border border-line bg-surface/90 p-2 backdrop-blur-xl
             md:flex-col md:rounded-full"
      :style="{ boxShadow: 'var(--t-glow)' }"
      aria-label="ناوبری بخش‌ها"
    >
      <button
        v-for="it in visibleItems"
        :key="it.id"
        :title="it.label"
        :aria-label="it.label"
        class="group grid h-10 w-10 place-content-center rounded-full transition duration-300"
        :class="[
          props.active === it.id ? 'bg-accent text-bg-deep glow' : 'text-muted hover:text-accent-2',
          it.id === 'hero' && logo ? 'overflow-hidden !bg-accent glow' : ''
        ]"
        @click="emit('go', it.id)"
      >
        <img
          v-if="it.id === 'hero' && logo"
          :src="logo"
          :alt="logoAlt"
          class="h-full w-full rounded-full object-contain p-1.5"
        />
        <svg
          v-else
          viewBox="0 0 24 24"
          class="h-[18px] w-[18px]"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path :d="it.d" />
        </svg>
      </button>
    </nav>

    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="translate-y-2 opacity-0"
      leave-active-class="transition duration-200 ease-in"
      leave-to-class="translate-y-2 opacity-0"
    >
      <button
        v-if="showScrollTop"
        type="button"
        title="بازگشت به بالای صفحه"
        aria-label="بازگشت به بالای صفحه"
        class="mt-3 hidden h-11 w-11 place-content-center rounded-full border border-accent/40 bg-surface/90 text-accent
               shadow-[0_0_18px_var(--t-accent-soft)] backdrop-blur-xl transition duration-300
               hover:-translate-y-1 hover:border-accent hover:bg-[var(--t-accent-soft)] hover:text-accent-2 md:grid"
        @click="scrollToTop"
      >
        <svg
          viewBox="0 0 24 24"
          class="h-5 w-5"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M12 19V5M6.5 10.5 12 5l5.5 5.5" />
        </svg>
      </button>
    </Transition>
  </div>
</template>
